import { useEffect, useRef } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { useErrorMessage } from 'features/store/pages/store-locator-experiment/components/store-card/hooks/use-error-message';
import { useIntl } from 'react-intl';
import { Animated } from 'react-native';

import ActionButton from 'components/action-button';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useOrderContext } from 'state/order';
import { theme } from 'styles/configure-theme';
import { isNative } from 'utils/environment';

import { StoreOrderButtonProps } from './types';

const CtaWrapper = Box.withConfig({
  paddingTop: '$6',
});

export const StoreOrderButton = ({
  restaurant,
  onOrderPress,
  isInSupportForm,
  disabled,
}: StoreOrderButtonProps) => {
  const { formatMessage } = useIntl();
  const {
    fetchingPosData,
    reorder: { reordering },
  } = useOrderContext();

  const message = useErrorMessage({ restaurant });

  useLogUserErrorMessage({
    message,
    severity: LogUserErrorSeverity.Irrecoverable,
    context: {
      moreInfo: 'store-actions.tsx',
      storeId: restaurant?.storeId,
    },
  });

  /**
   * FADE-IN ReactNative ANIMATION
   */
  const fadeAnim = useRef(new Animated.Value(0));
  useEffect(() => {
    Animated.timing(fadeAnim.current, {
      toValue: 1,
      duration: 200,
      useNativeDriver: isNative,
    }).start();
  }, []);

  const buttonTextId = isInSupportForm ? 'select' : 'orderHere';

  // Loading state covers two scenarios:
  // - fetchingPosData: Loading store POS connectivity/menu data for regular ordering
  // - reordering: Processing a reorder request (users may be redirected here to select a store if no store selected)
  const isLoading = fetchingPosData || reordering;

  return (
    <Animated.View style={[{ opacity: fadeAnim.current }]}>
      <CtaWrapper>
        <ActionButton
          isLoading={isLoading}
          testID="store-modal-order-here"
          fullWidth
          onPress={onOrderPress}
          disabled={disabled}
          leftIcon={
            <Box justifyContent="center">
              <Icon
                size="$5"
                color={theme.token('icon-button-primary')}
                variant="location"
                aria-hidden
              />
            </Box>
          }
        >
          {formatMessage({ id: buttonTextId })}
        </ActionButton>
      </CtaWrapper>
    </Animated.View>
  );
};
